// v0.4.92 — Filing-workflow checklist row with optional
// prerequisite sub-rows (sign-off 2026-06-20 = 3b).
//
// Extracted from Step6FreezeExport.jsx so that file stays under
// the 500-LOC cap after merging the FilingReadiness checks in.
//
// Shape:
//   <ChecklistRow done={bool} label="" sublabel="" action={node}
//     prerequisites={[{ id, label, sublabel, done, warning?,
//       goto? (number), gotoUrl? (string) }, …]} onGoTo={(n)=>…}
//   />
//
// Per-prereq data-testid (`readiness-row-${id}` and
// `readiness-row-${id}-fix`) matches the pre-v0.4.92 FilingReadiness
// surface so backend tests + the testing agent's per-prereq
// assertions keep working.
import React from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, WarningCircle, ArrowRight } from "@phosphor-icons/react";
import { stepLabelFor } from "../workflow/stepLabels";

export const ChecklistRow = ({
  done, label, sublabel, action, prerequisites, onGoTo,
  "data-testid": testId,
}) => {
  const { t } = useTranslation();
  const visible = (prerequisites || []).filter(Boolean);
  const blocked = visible.filter((p) => !p.done);
  return (
    <div
      data-testid={testId}
      className={`border rounded-xl ${
        done
          ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30"
          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
      }`}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-3">
          <span
            className={`text-lg ${
              done
                ? "text-emerald-700 dark:text-emerald-300"
                : "text-slate-600 dark:text-slate-300"
            }`}
          >
            {done ? "✓" : "○"}
          </span>
          <div>
            <div
              className={`text-sm tracking-normal ${
                done
                  ? "text-emerald-700 dark:text-emerald-300"
                  : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {label}
            </div>
            {sublabel && (
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                {sublabel}
              </div>
            )}
          </div>
        </div>
        {action}
      </div>
      {visible.length > 0 && (
        <div
          data-testid={testId ? `${testId}-prereqs` : undefined}
          className={`border-t px-4 py-2 space-y-1 ${
            blocked.length === 0
              ? "border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/20"
              : "border-amber-100 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-950/20"
          }`}
        >
          {visible.map((p) => (
            <PrerequisiteSubRow key={p.id} prereq={p} onGoTo={onGoTo} />
          ))}
        </div>
      )}
    </div>
  );
};

const PrerequisiteSubRow = ({ prereq, onGoTo }) => {
  const { t } = useTranslation();
  const Icon = prereq.done
    ? CheckCircle
    : prereq.warning
      ? WarningCircle
      : XCircle;
  const color = prereq.done
    ? "text-emerald-600 dark:text-emerald-400"
    : prereq.warning
      ? "text-amber-600 dark:text-amber-400"
      : "text-rose-500 dark:text-rose-400";
  return (
    <div
      data-testid={`readiness-row-${prereq.id}`}
      data-done={prereq.done}
      className="flex items-center justify-between gap-3 text-[11px]"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon size={13} weight="bold" className={`${color} shrink-0`} />
        <span
          className={`${
            prereq.done
              ? "text-slate-500 dark:text-slate-400"
              : "text-slate-700 dark:text-slate-200 font-semibold"
          }`}
        >
          {prereq.label}
        </span>
        <span className="text-slate-400 dark:text-slate-500 truncate">
          · {prereq.sublabel}
        </span>
      </div>
      {!prereq.done && (prereq.goto || prereq.gotoUrl) && (
        prereq.gotoUrl ? (
          <a
            href={prereq.gotoUrl}
            data-testid={`readiness-row-${prereq.id}-fix`}
            title={t("ui.filing.readiness_row.navigate_title", { url: prereq.gotoUrl, defaultValue: `Navigate to ${prereq.gotoUrl} to address this prerequisite` })}
            className="text-[10px] tracking-normal text-teal-700 dark:text-teal-300 hover:text-teal-800 dark:hover:text-teal-200 underline decoration-dotted flex items-center gap-1 shrink-0"
          >
            {/* v0.4.96 (Item 2) — destination-specific label so the
                operator knows where they're being sent before they
                click. i18n-ignore.
                v0.6.8 — gotoUrl started carrying real query params
                (?edit=<id>&section=ioss) once it started deep-linking
                to a specific client's edit section instead of a bare
                page path; showing that raw as visible text is noise a
                human doesn't need. gotoLabel overrides just the
                display text — the href/title still carry the real,
                full URL. */}
            {prereq.gotoLabel ? `Open ${prereq.gotoLabel}` : <>Open {prereq.gotoUrl}</>}
            <ArrowRight size={10} weight="bold" />
          </a>
        ) : (
          <button
            type="button"
            data-testid={`readiness-row-${prereq.id}-fix`}
            data-resolution-class="self-resolve"
            data-resolution-endpoint="navigation"
            onClick={() => onGoTo?.(prereq.goto)}
            title={t("ui.filing.readiness_row.jump_title", { n: prereq.goto, label: stepLabelFor(prereq.goto, t), defaultValue: `Jump back to Step ${prereq.goto} (${stepLabelFor(prereq.goto, t)}) to address this prerequisite` })}
            className="text-[10px] tracking-normal text-teal-700 dark:text-teal-300 hover:text-teal-800 dark:hover:text-teal-200 underline decoration-dotted flex items-center gap-1 shrink-0"
          >
            {/* v0.4.96 (Item 2) — destination-specific label. */}
            Open Step {prereq.goto} · {stepLabelFor(prereq.goto, t)}
            <ArrowRight size={10} weight="bold" />
          </button>
        )
      )}
    </div>
  );
};

export default ChecklistRow;
